import React, { useEffect, useMemo, useRef, useState } from "react";
import { LivechatSubjectType } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/webvideo/UserPublicDataProvider";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import useLiveChat from "hooks/useLiveChat";
import useProduceController from "hooks/useProduceController";
import useStreamHelper from "hooks/useStreamHelper";
import { ProduceUser } from "types";
import PublicStreamView from "../PublicStreamView";

interface Props {
  produceUser: UserPublicData;
  onClose: () => void;
  livechatSubject: Subject<LivechatSubjectType>;
  disableAttachments?: boolean;
}

function ProduceController({ produceUser, livechatSubject, onClose, disableAttachments }: Props) {
  const { loading: liveChatLoading, joinData, token, userList, clientServerTimeDiff } = useLiveChat({
    livechatSubject,
  });

  const streamStartedDate = useMemo(() => {
    if (!joinData) {
      return "";
    }
    return new Date(parseISO(joinData.me.joinTime).getTime() + (clientServerTimeDiff ?? 0)).toISOString();
  }, [clientServerTimeDiff, joinData]);

  const {
    stream,
    screenStream,
    loading,
    mediaConstraints,
    audioInputs,
    videoInputs,
    onChangeConstraints,
    producerSpeaking,
    permissions,
  } = useProduceController({
    token,
    joinData,
    autoProduceStream: true,
  });

  const { checkStreamForChanges } = useStreamHelper();

  const localProduceStream = useRef<MediaStream>(stream);
  const localProduceScreenStream = useRef<MediaStream>(screenStream);
  const [me, setMe] = useState<ProduceUser | null>(null);

  useEffect(() => {
    if (!stream || !localProduceStream.current) return;
    checkStreamForChanges(stream, localProduceStream.current);
  }, [stream, checkStreamForChanges]);

  useEffect(() => {
    if (!screenStream || !localProduceScreenStream.current) return;
    checkStreamForChanges(screenStream, localProduceScreenStream.current);
  }, [screenStream, checkStreamForChanges]);

  useEffect(() => {
    setMe(
      joinData
        ? {
            user: joinData.me,
            stream: localProduceStream.current,
            screenStream: localProduceScreenStream.current,
            constrains: mediaConstraints,
            isSpeaking: producerSpeaking,
          }
        : null
    );
  }, [joinData, mediaConstraints, localProduceStream, localProduceScreenStream, producerSpeaking]);

  return (
    <PublicStreamView
      produceUser={produceUser}
      threadId={joinData?.threadId}
      produce
      loading={loading || liveChatLoading}
      userList={userList}
      streamStartedDate={streamStartedDate}
      onClose={onClose}
      token={token}
      managedConstraints={mediaConstraints}
      audioInputs={audioInputs}
      videoInputs={videoInputs}
      onChangeConstraints={onChangeConstraints}
      permissions={permissions}
      disableAttachments={disableAttachments}
      showConfirmationExit
      me={me}
      users={[]}
    />
  );
}

export default ProduceController;
